import React from 'react';
import styled from 'styled-components';
import { IonButton, IonIcon } from '@ionic/react';
import { addOutline } from 'ionicons/icons';
import ImageStatusCard from './ImageStatusCard';
import ImageErrorBoundary from './ImageErrorBoundary';
import { ImageStatus } from '../../../services/buildfire/rdb/cases';

interface ImageStatusGridProps {
  images: (File | ImageStatus | string)[];
  onRemove: (index: number) => void;
  onRetry: (index: number) => void;
  onAddImages: () => void;
  userId?: string;
  getDisabled?: (index: number) => boolean;
  maxImages?: number;
}

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 0;

  @media (min-width: 480px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
  }
`;

const AddImageButton = styled(IonButton)`
  width: 100%;
  height: auto;
  aspect-ratio: 1;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  --border-radius: 16px;
  --border-width: 1px;
  --border-style: dashed;
  --border-color: #c7c7cc;
  --background: #f9f9f9;
  --background-activated: #e8e8e8;
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: 0;
  --padding-bottom: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  @media (min-width: 768px) {
    --border-radius: 12px;
    gap: 6px;
  }
`;

const AddImageContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #8e8e93;
  font-weight: 600;
  font-size: 12px;

  ion-icon {
    font-size: 28px;
  }

  @media (min-width: 768px) {
    gap: 6px;
    font-size: 13px;

    ion-icon {
      font-size: 32px;
    }
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #8e8e93;
  font-size: 15px;
  line-height: 1.5;
  background: #f9f9f9;
  border-radius: 16px;
  border: 2px dashed #d1d1d6;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    padding: 40px 20px;
    font-size: 14px;
    border-radius: 12px;
  }
`;

const ImageStatusGrid: React.FC<ImageStatusGridProps> = ({
  images,
  onRemove,
  onRetry,
  onAddImages,
  userId,
  getDisabled,
  maxImages = 20,
}) => {
  const canAddMore = images.length < maxImages;

  const getProgress = (image: File | ImageStatus | string): number | undefined => {
    if (typeof image === 'string' || image instanceof File) {
      return undefined;
    }
    if (image.status === 'uploading') {
      return 0.5;
    }
    return undefined;
  };

  const getImageKey = (image: File | ImageStatus | string, index: number): string => {
    if (image instanceof File) {
      return `file-${image.name}-${image.size}-${image.lastModified}`;
    }
    if (typeof image === 'string') {
      return `str-${image}`;
    }
    return `status-${image.filename || index}`;
  };

  if (images.length === 0) {
    return (
      <GridContainer>
        <EmptyState>
          No images added yet. Click the button below to add images.
        </EmptyState>
        {canAddMore && (
          <AddImageButton fill="clear" onClick={onAddImages}>
            <AddImageContent>
              <IonIcon icon={addOutline} style={{ fontSize: '32px' }} />
              <span>Add Images</span>
            </AddImageContent>
          </AddImageButton>
        )}
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <Grid>
        {images.map((image, index) => (
          <ImageErrorBoundary key={getImageKey(image, index)}>
            <ImageStatusCard
              image={image}
              userId={userId}
              progress={getProgress(image)}
              onRemove={() => onRemove(index)}
              onRetry={() => onRetry(index)}
              disabled={getDisabled ? getDisabled(index) : false}
            />
          </ImageErrorBoundary>
        ))}
        {canAddMore && (
          <AddImageButton fill="clear" onClick={onAddImages}>
            <AddImageContent>
              <IonIcon icon={addOutline} style={{ fontSize: '32px' }} />
              <span>Add More</span>
            </AddImageContent>
          </AddImageButton>
        )}
      </Grid>
    </GridContainer>
  );
};

export default ImageStatusGrid;
